// reason: FIXME at moment we rely too much on custom styling, we need the background color
// eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
import { Toolbar } from '@mui/material'
import { ChevronLeftIcon, CloseIcon } from '@dhl-ui/icons'
import { Ellipsis, IconButton, Box } from '@dhl-ui/components'
import { type SubPageProps } from './SubPage'

/**
 * 
 * The `Toolbar` component is a horizontal container used to group contextual actions, filters, search fields, 
 * or controls relevant to a specific page or section. Positioned typically at the top, it helps users interact 
 * with the content below in an efficient and consistent way.
 * 
 * There are usecases where, for code re-usablility, its not handy to consume our full `SubPage`. So this 
 * component is exported for own consumage as well.
 * 
 * 
 * One of these usecases is when you need to show the content on "Mobile" inside a `SubPage` but with same content for "Desktop"
 * inside a drawer with a minimum of tweaks (so using same back and close logic!).
 * 
 * For these usecases you can wrap this `SubPageToolbar` yourself inside a Box to emulate the `SubPage` in look and feel.
 * But with less code tweaks :)
 * 
 * @example
 * 
 ```jsx
<Box
  sx={{
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  }}
  >
    ... your component with 
    `SubPageToolbar`
    `Modal.Content`
    `Modal.Footer`
    ... end of your component, which is now re-usable :)
</Box>
```
 */
export const SubPageToolbar = ({
  title,
  endNodes,
  headerBackground = 'none',
  onBack,
  onClose,
}: Omit<SubPageProps, 'children'>) => (
  <Toolbar sx={{ backgroundColor: headerBackground === 'none' ? 'background.default' : 'background.paper' }}>
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gap: 1,
        gridTemplateColumns: 'minmax(auto, 1fr) auto minmax(auto, 1fr)',
      }}
    >
      <Box sx={{ whiteSpace: 'nowrap' }}>
        {onBack && (
          <IconButton data-testid="sub-page-toolbar-back" aria-label="back" size="small" edge="start" onClick={onBack}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>
      <Box sx={{ alignContent: 'center', textAlign: 'center', userSelect: 'none' }}>
        <Ellipsis variant="h6" lineClamp={2}>
          {title}
        </Ellipsis>
      </Box>
      <Box sx={{ whiteSpace: 'nowrap', display: 'flex', justifyContent: 'flex-end' }}>
        {endNodes}
        {onClose && (
          <IconButton
            aria-label="close"
            size="small"
            edge="end"
            onClick={onClose}
            variant="contained"
            color="alternative"
            data-testid="sub-page-toolbar-close"
            sx={{ mr: 0 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  </Toolbar>
)
